/**
 * CountryCompare · Module C 分国家游戏投放对比
 *
 * T12 产出，需求 §4.4.6（参考 Figma 3259-18986 / 3277-19886）
 *
 * 结构：
 * - CardCommon 外壳
 *   - title：模块名
 *   - subFilterBeforeElement：仅国家多选（指标固定且无 metric-fixed 文字，与 figma 对齐）
 *   - operations：堆叠条 / 分游戏列 两种视图切换
 *   - hasDownload：下载（T13 接入）
 * - 主体：
 *   - 视图 A（默认）：CountryStackedTable —— 堆叠条
 *   - 视图 B：CountryTable —— 分游戏列
 *   - 详情面板（仅 columns 视图）：绝对定位贴 table 右边，高度同 table
 *
 * D8 美国固定第一行（mock 已实现）；列顺序锁定（取 list[0] 即美国行 games 顺序）
 */
import React, { useMemo, useRef, useState } from 'react';
import CardCommon, { operation } from '@op/components/CardCommon';
import { stackedTableIcon, tableIcon } from '@op/constants/chart';
import useIntls from '@opm/hooks/useIntls';
import { IFilter } from '../../types/filter';
import { useGameColorContext } from '../../context/GameColorContext';
import { DBO_TM } from '@op/utils/format';
import { gotoDownload } from '@opm/pages/Download';
import { useCountryCompare } from '../../hooks/useCountryCompare';
import { useCountryMap } from '../../hooks/useCountryMap';
import CountrySelect from './CountrySelect';
import CountryStackedTable from './CountryStackedTable';
import CountryTable from './CountryTable';
import CountryDetailPanel from './CountryDetailPanel';
import './index.scss';

export const COUNTRY_COMPARISON_TITLE = 'Comparison of Active Ads by Country';

export interface ICountryCompareProps {
  filter: IFilter;
  id?: string;
  className?: string;
  emptyTitleText: string;
}

/** 视图模式 */
type TViewMode = 'stacked' | 'columns';

const CARD_MIN_HEIGHT = 480;

/** 默认分页（前端分页，10/页） */
const DEFAULT_PAGE_INFO = { page: 1, page_size: 10 };

export default function CountryCompare({
  filter,
  id,
  className,
  emptyTitleText
}: ICountryCompareProps) {
  const { intl } = useIntls();
  const { gameMap } = useGameColorContext();
  /** 国家多选 state（默认 `[]` = 全选） */
  const [countryCodes, setCountryCodes] = useState<string[]>([]);
  /** 视图模式 */
  const [viewMode, setViewMode] = useState<TViewMode>('columns');
  /** 当前打开详情的国家 country_code（仅 columns 视图） */
  const [openDetailCode, setOpenDetailCode] = useState<string | null>(null);
  /** 前端分页（两个视图共享） */
  const [pageInfo, setPageInfo] = useState(DEFAULT_PAGE_INFO);
  /** 表格容器 ref（用于详情面板高度对齐） */
  const tableWrapperRef = useRef<HTMLDivElement>(null);

  // 数据
  const { list, gameOrder, linkTime, sortBy, setSortBy, loading } = useCountryCompare({
    filter,
    countryCodes,
  });
  /** 国家字典：仅用于下载时把 alpha-2 展开成 country_name */
  const { countryMap } = useCountryMap();

  /** 文案预先固化 */
  const labels = useMemo(
    () => ({
      title: intl(COUNTRY_COMPARISON_TITLE),
      empty: intl('No data available for the selected period'),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [intl],
  );

 
  /**
   * 默认排序：list 加载完后如果还没有排序，自动按第一个游戏 desc
   * 仅初始化一次（用户手动改后不再覆盖）
   */
  // useEffect(() => {
  //   if (!sortBy.by && gameOrder.length > 0) {
  //     setSortBy({ by: gameOrder[0], order: 'desc' });
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [gameOrder]);

  /** 当前打开的国家行（详情面板数据） */
  const openRow = useMemo(
    () => list.find((r) => r.country_code === openDetailCode) ?? null,
    [list, openDetailCode],
  );

  /** subFilterBeforeElement：国家多选 */
  const subFilterBefore = (
    <div className="dbom-country-compare__sub-filter">
      <CountrySelect
        value={countryCodes}
        onChange={setCountryCodes}
        className="dbom-country-compare__country-select"
      />
    </div>
  );

  /** 视图切换时关闭详情 */
  const handleViewChange = (next: TViewMode) => {
    setViewMode(next);
    if (next === 'stacked') setOpenDetailCode(null);
  };

  /** filter / 国家 / 排序变化时：关闭详情 + 重置回第 1 页 */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useMemo(() => {
    setOpenDetailCode(null);
    setPageInfo(DEFAULT_PAGE_INFO);
  }, [filter, countryCodes, sortBy]);

  const isEmpty = !loading && list.length === 0;

  /** 环比时间窗口（来自接口 link_time） */
  const newTime = useMemo<[string, string] | undefined>(
    () =>
      linkTime?.new_start_date && linkTime?.new_end_date
        ? [linkTime.new_start_date, linkTime.new_end_date]
        : undefined,
    [linkTime],
  );
  const oldTime = useMemo<[string, string] | undefined>(
    () =>
      linkTime?.old_start_date && linkTime?.old_end_date
        ? [linkTime.old_start_date, linkTime.old_end_date]
        : undefined,
    [linkTime],
  );

  return (
    <CardCommon
      id={id}
      className={`dbom-country-compare-card ${className ?? ''}`}
      title={labels.title}
      // 默认 RenderCard 模式（contentCard=false）下用 subFilterBeforeElement
      subFilterBeforeElement={subFilterBefore}
      loading={loading}
      empty={isEmpty}
      emptyTitleText={emptyTitleText}
      hasEmptyDesc={false}
      loadingHeight={CARD_MIN_HEIGHT}
      emptyHeight={CARD_MIN_HEIGHT}
      operations={[{...stackedTableIcon, tip: "Stacked Table"}, tableIcon]}
      defaultOperation={'table'}
      handleOperationsOnClick={(item: operation) => {
        handleViewChange(item?.symbol === 'table' ? 'columns' : 'stacked');
      }}
      hasDownload
      handleDownloadOnClick={() => {
        void gotoDownload({
          key: 'MarketingCompetitorAdComparisonCountry',
          fileName: `Competitor_Ad_Comparison_Country_${DBO_TM.timeTitle({
            start_time: filter.start_time,
            end_time: filter.end_time,
            date_type: filter.date_type,
          })}`,
          filter,
          data: list,
          auxiliaryParams: { gameMap, gameOrder, countryMap },
        });
      }}
    >
      <div className={`dbom-country-compare__body ${openRow ? 'dbom-country-compare__body--with-detail' : ''}`}>
        <div ref={tableWrapperRef} className="dbom-country-compare__table-wrap">
          {viewMode === 'stacked' ? (
            <CountryStackedTable
              list={list}
              gameMap={gameMap}
              gameOrder={gameOrder}
              sortBy={sortBy}
              setSortBy={setSortBy}
              loading={loading}
              pageInfo={pageInfo}
              setPageInfo={setPageInfo}
            />
          ) : (
            <CountryTable
              list={list}
              gameMap={gameMap}
              gameOrder={gameOrder}
              sortBy={sortBy}
              setSortBy={setSortBy}
              openDetailCode={openDetailCode}
              onOpenDetail={setOpenDetailCode}
              loading={loading}
              newTime={newTime}
              oldTime={oldTime}
              pageInfo={pageInfo}
              setPageInfo={setPageInfo}
            />
          )}
        </div>

        {viewMode === 'columns' && openRow && (
          <div className="dbom-country-compare__detail-wrap">
            <CountryDetailPanel
              row={openRow}
              gameMap={gameMap}
              onClose={() => setOpenDetailCode(null)}
              height={tableWrapperRef.current?.clientHeight}
              newTime={newTime}
              oldTime={oldTime}
            />
          </div>
        )}
      </div>
    </CardCommon>
  );
}
